import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, Play, Loader2, Server, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandscapeDiscovery = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [environments, setEnvironments] = useState([]);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [discoveryData, setDiscoveryData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRealDataDetails, setShowRealDataDetails] = useState(false);

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/sap-environments`);
      setEnvironments(response.data);
      if (response.data.length > 0) setSelectedEnv(response.data[0]);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const runDiscovery = async () => {
    if (!selectedEnv) return;
    setScanning(true);
    try {
      const response = await axios.post(`${API}/sap-environments/${selectedEnv.id}/landscape-discovery`, {});
      setDiscoveryData(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setScanning(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-12 h-12 text-blue-600 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Landscape Discovery Engine</h2>
            <p className="text-gray-600">Comprehensive SAP system analysis</p>
          </div>
          {selectedEnv && (
            <button onClick={runDiscovery} disabled={scanning} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50">
              {scanning ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Scanning...</span></> : <><Play className="w-5 h-5" /><span>Run Discovery</span></>}
            </button>
          )}
        </div>
      </div>

      {/* Environment Selector */}
      {environments.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <div className="flex items-center space-x-4">
            <Server className="w-6 h-6 text-blue-600" />
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select SAP Environment to Analyze</label>
              <select
                value={selectedEnv?.id || ''}
                onChange={(e) => {
                  const env = environments.find(env => env.id === e.target.value);
                  setSelectedEnv(env);
                  setDiscoveryData(null); // Clear previous discovery data
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              >
                <option value="">Choose an environment...</option>
                {environments.map(env => (
                  <option key={env.id} value={env.id}>
                    {env.environment_name} - {env.system_type} ({env.system_id}) - Client {env.client}
                    {env.is_mock && ' [MOCK]'}
                  </option>
                ))}
              </select>
            </div>
            {selectedEnv && (
              <div className="flex flex-col items-end">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  selectedEnv.connection_status === 'connected' ? 'bg-green-100 text-green-700' :
                  selectedEnv.connection_status === 'error' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {selectedEnv.connection_status || 'Not connected'}
                </span>
                {selectedEnv.is_mock && (
                  <span className="mt-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                    Simulation Mode
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {environments.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Server className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No SAP Environments Configured</h3>
          <p className="text-gray-600 mb-6">Please configure SAP environments in the SAP Simulation module first</p>
        </div>
      )}

      {environments.length > 0 && !discoveryData && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Database className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">Ready to Discover</h3>
          <p className="text-gray-600 mb-6">Click "Run Discovery" to analyze your SAP landscape</p>
          <p className="text-sm text-gray-500">Environment: {selectedEnv?.environment_name} ({selectedEnv?.system_type})</p>
        </div>
      )}

      {discoveryData && discoveryData.discovery_results && (
        <div className="space-y-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'interfaces', label: 'Interfaces' },
                { id: 'workload', label: 'Workload' },
                { id: 'batch-jobs', label: 'Batch Jobs' },
                { id: 'users', label: 'Top Users' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-6">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <Database className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{discoveryData.discovery_results.custom_code_inventory?.total_custom_objects?.toLocaleString()}</div>
              <div className="text-blue-100 text-sm">Custom Objects</div>
              {discoveryData.discovery_results.custom_code_inventory?._real_based && (
                <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded">
                  📊 Real Data
                </div>
              )}
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{discoveryData.discovery_results.idoc_discovery?.total_idocs || 0}</div>
              <div className="text-purple-100 text-sm">Interfaces</div>
              {discoveryData.discovery_results.idoc_discovery?._real_based && (
                <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded">
                  📊 Real Data
                </div>
              )}
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{discoveryData.discovery_results.database_footprint?.total_size_gb} GB</div>
              <div className="text-green-100 text-sm">Database Size</div>
              {discoveryData.discovery_results.database_footprint?._estimated_from_services && (
                <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded">
                  📊 Real Estimate
                </div>
              )}
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{discoveryData.readiness_score}%</div>
              <div className="text-orange-100 text-sm">Readiness</div>
            </div>
          </div>

          {/* Data Source Indicator */}
          <div className={`p-4 rounded-xl border-2 ${
            discoveryData.discovery_results.data_source === 'REAL_SAP_SYSTEM' 
              ? 'bg-green-50 border-green-500' 
              : 'bg-yellow-50 border-yellow-500'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  discoveryData.discovery_results.data_source === 'REAL_SAP_SYSTEM' 
                    ? 'bg-green-500 animate-pulse' 
                    : 'bg-yellow-500'
                }`}></div>
                <div>
                  <p className="font-bold text-gray-900">
                    Data Source: {discoveryData.discovery_results.data_source || 'MOCK_DATA'}
                  </p>
                  {discoveryData.discovery_results.connection_type && (
                    <p className="text-sm text-gray-600">
                      Connection: {discoveryData.discovery_results.connection_type}
                      {discoveryData.discovery_results.extraction_method && ` - ${discoveryData.discovery_results.extraction_method}`}
                    </p>
                  )}
                </div>
              </div>
              {discoveryData.discovery_results.extraction_status && (
                <div className="text-sm text-gray-700">
                  {discoveryData.discovery_results.extraction_status}
                </div>
              )}
            </div>
            
            {discoveryData.discovery_results.extraction_note && (
              <div className="mt-2 text-sm text-orange-700 bg-orange-100 p-3 rounded">
                ℹ️ {discoveryData.discovery_results.extraction_note}
              </div>
            )}
            
            {discoveryData.discovery_results.real_system_info && (
              <div className="mt-2 text-sm text-green-700 bg-green-100 p-3 rounded">
                ✅ Real system information extracted successfully
                <div className="mt-2 text-xs text-green-800">
                  <strong>OData Services Discovered:</strong> {discoveryData.discovery_results.real_system_info.total_services}
                </div>
              </div>
            )}
            
            {/* Real Metrics Calculation Info */}
            {discoveryData.discovery_results.real_derived_metrics && (
              <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2 text-sm">📊 Real Data Metrics (Calculated from SAP)</h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-2 bg-white rounded">
                    <div className="text-gray-600">Custom Objects</div>
                    <div className="font-bold text-blue-600">
                      {discoveryData.discovery_results.real_derived_metrics.custom_objects_estimate}
                    </div>
                    <div className="text-gray-500 mt-1">
                      From {discoveryData.discovery_results.real_system_info.custom_services} custom services
                    </div>
                  </div>
                  <div className="p-2 bg-white rounded">
                    <div className="text-gray-600">Interfaces</div>
                    <div className="font-bold text-purple-600">
                      {discoveryData.discovery_results.real_derived_metrics.interfaces_estimate}
                    </div>
                    <div className="text-gray-500 mt-1">
                      From interface services
                    </div>
                  </div>
                  <div className="p-2 bg-white rounded">
                    <div className="text-gray-600">Database Size</div>
                    <div className="font-bold text-green-600">
                      {Math.round(discoveryData.discovery_results.real_derived_metrics.database_size_estimate_gb)} GB
                    </div>
                    <div className="text-gray-500 mt-1">
                      Estimated from {discoveryData.discovery_results.real_system_info.total_services} services
                    </div>
                  </div>
                  <div className="p-2 bg-white rounded">
                    <div className="text-gray-600">Custom Dev Ratio</div>
                    <div className="font-bold text-orange-600">
                      {discoveryData.discovery_results.real_derived_metrics.custom_development_ratio}%
                    </div>
                    <div className="text-gray-500 mt-1">
                      {discoveryData.discovery_results.real_system_info.custom_services} of {discoveryData.discovery_results.real_system_info.total_services} services
                    </div>
                  </div>
                  <div className="p-2 bg-white rounded">
                    <div className="text-gray-600">Fiori Apps</div>
                    <div className="font-bold text-indigo-600">
                      {discoveryData.discovery_results.real_derived_metrics.fiori_adoption_count}
                    </div>
                    <div className="text-gray-500 mt-1">
                      Real Fiori services detected
                    </div>
                  </div>
                  <div className="p-2 bg-white rounded">
                    <div className="text-gray-600">API Exposure</div>
                    <div className="font-bold text-teal-600">
                      {discoveryData.discovery_results.real_derived_metrics.api_exposure_count}
                    </div>
                    <div className="text-gray-500 mt-1">
                      Interface/API services
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Real Data Details - Expandable */}
            {discoveryData.discovery_results.real_system_info && (
              <div className="mt-3">
                <button
                  onClick={() => setShowRealDataDetails(!showRealDataDetails)}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                >
                  <span>{showRealDataDetails ? '▼' : '▶'} View Real Extracted Data</span>
                </button>
                
                {showRealDataDetails && (
                  <div className="mt-3 space-y-3">
                    {/* Real OData Services */}
                    {discoveryData.discovery_results.real_system_info.odata_services && (
                      <div className="p-4 bg-white rounded-lg border border-green-200">
                        <h4 className="font-bold text-gray-900 mb-2">
                          Real OData Services ({discoveryData.discovery_results.real_system_info.total_services})
                        </h4>
                        <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                          {discoveryData.discovery_results.real_system_info.odata_services.map((svc, idx) => (
                            <div key={idx} className="text-xs p-2 bg-gray-50 rounded flex items-start space-x-2">
                              <span className={`px-2 py-0.5 rounded ${
                                svc.title.startsWith('Z') || svc.title.startsWith('Y') 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {svc.title.startsWith('Z') || svc.title.startsWith('Y') ? 'Custom' : 'Standard'}
                              </span>
                              <span className="font-mono">{svc.title || svc.technical_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Real Service Counts */}
                    {discoveryData.discovery_results.real_table_data && (
                      <div className="p-4 bg-white rounded-lg border border-blue-200">
                        <h4 className="font-bold text-gray-900 mb-2">Service Analysis</h4>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div className="flex justify-between">
                            <span>Total Services:</span>
                            <span className="font-bold">{discoveryData.discovery_results.real_table_data.service_count}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-2">
                            {discoveryData.discovery_results.real_table_data.extraction_note}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-3xl font-bold mb-2">Readiness Score</h3>
                <p className="text-blue-100">Overall system readiness for S/4HANA migration</p>
              </div>
              <div className="text-6xl font-bold">{discoveryData.readiness_score}%</div>
            </div>
            
            {/* Score Breakdown */}
            <div className="mt-6 grid grid-cols-5 gap-4">
              {discoveryData.discovery_results.readiness_score?.scores_by_category && Object.entries(discoveryData.discovery_results.readiness_score.scores_by_category).map(([category, score]) => (
                <div key={category} className="bg-white/10 backdrop-blur rounded-lg p-3">
                  <div className="text-2xl font-bold mb-1">{score}%</div>
                  <div className="text-xs text-blue-100 capitalize">{category.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
            
            {/* Weights Applied */}
            <div className="mt-4 text-xs text-blue-100">
              <p>Weights: Custom Code 30% | Interfaces 20% | Add-ons 20% | Database 15% | Workload 15%</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">System Details</h3>
              {discoveryData.discovery_results.system_metadata?._real_data && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-lg">
                  📊 Real User Data
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">System ID:</span><span className="font-semibold">{discoveryData.discovery_results.system_metadata?.system_id}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Release:</span><span className="font-semibold">{discoveryData.discovery_results.system_metadata?.release}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Database:</span><span className="font-semibold">{discoveryData.discovery_results.system_metadata?.database_type} {discoveryData.discovery_results.system_metadata?.database_version}</span></div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Users:</span>
                  <span className="font-semibold flex items-center space-x-1">
                    <span>{discoveryData.discovery_results.system_metadata?.total_users?.toLocaleString()}</span>
                    {discoveryData.discovery_results.system_metadata?._real_user_data && (
                      <span className="text-xs text-green-600">📊</span>
                    )}
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Kernel:</span><span className="font-semibold">{discoveryData.discovery_results.system_metadata?.kernel_release}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">EHP Level:</span><span className="font-semibold">{discoveryData.discovery_results.system_metadata?.ehp_level}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Unicode:</span><span className="font-semibold text-green-600">{discoveryData.discovery_results.system_metadata?.unicode_enabled ? 'Yes' : 'No'}</span></div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Transactions/Day:</span>
                  <span className="font-semibold flex items-center space-x-1">
                    <span>{discoveryData.discovery_results.system_metadata?.transactions_per_day?.toLocaleString()}</span>
                    {discoveryData.discovery_results.system_metadata?._real_user_data && (
                      <span className="text-xs text-green-600">📊</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            {discoveryData.discovery_results.system_metadata?._fiori_apps && (
              <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-800">
                ✨ {discoveryData.discovery_results.system_metadata._fiori_apps} Fiori apps detected from real SAP
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Custom Code Summary</h3>
              {discoveryData.discovery_results.custom_code_inventory?._real_based && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-lg">
                  📊 Real Derived Data
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{discoveryData.discovery_results.custom_code_inventory?.by_type?.programs || 0}</div>
                <div className="text-sm text-gray-600">Programs</div>
                {discoveryData.discovery_results.custom_code_inventory?._real_based && (
                  <div className="mt-1 text-xs text-green-600">Real estimate</div>
                )}
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{discoveryData.discovery_results.custom_code_inventory?.by_type?.classes || 0}</div>
                <div className="text-sm text-gray-600">Classes</div>
                {discoveryData.discovery_results.custom_code_inventory?._real_based && (
                  <div className="mt-1 text-xs text-green-600">Real estimate</div>
                )}
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{discoveryData.discovery_results.code_pattern_analysis?.risk_distribution?.critical || 0}</div>
                <div className="text-sm text-gray-600">Critical Risk</div>
              </div>
            </div>
            {discoveryData.discovery_results.custom_code_inventory?._calculation && (
              <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-800">
                ℹ️ {discoveryData.discovery_results.custom_code_inventory._calculation}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold mb-4">High-Risk Code Patterns</h3>
            <div className="space-y-3">
              {discoveryData.discovery_results.code_pattern_analysis?.high_risk_patterns?.slice(0, 5).map((pattern, idx) => (
                <div key={idx} className="border-2 border-red-200 bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{pattern.pattern}</h4>
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-bold text-red-600">{pattern.count}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        pattern.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                        pattern.severity === 'High' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>{pattern.severity}</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">✓ {pattern.recommendation}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">AI-Predicted Migration Blockers</h3>
              {discoveryData.discovery_results._migration_blockers_real && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-lg">
                  📊 Real Analysis
                </span>
              )}
            </div>
            <div className="space-y-3">
              {(discoveryData.discovery_results.migration_blockers || discoveryData.discovery_results.predicted_blockers)?.slice(0, 4).map((blocker, idx) => (
                <div key={idx} className={`border-2 rounded-lg p-4 ${
                  blocker._real_data ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{blocker.name}</h4>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{blocker.component}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          blocker.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                          blocker.severity === 'High' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>{blocker.severity}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{blocker.issue}</p>
                      <p className="text-xs text-gray-500">Estimated effort: {blocker.estimated_effort_days} days</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
            </div>
          )}

          {/* Interfaces Tab */}
          {activeTab === 'interfaces' && discoveryData.discovery_results.idoc_discovery && (
            <div className="space-y-6">
              {/* Real Data Indicator for Interfaces */}
              {discoveryData.discovery_results.idoc_discovery._real_based && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold text-green-800">
                      Real Data: {discoveryData.discovery_results.idoc_discovery._calculation}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border-2 border-blue-300 rounded-xl p-6">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{discoveryData.discovery_results.idoc_discovery.total_idocs}</div>
                  <div className="text-sm text-gray-600">Total IDocs</div>
                  {discoveryData.discovery_results.idoc_discovery._real_based && (
                    <div className="mt-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      📊 Real Data
                    </div>
                  )}
                  {discoveryData.discovery_results.idoc_discovery.avg_failure_rate && (
                    <div className="text-xs text-gray-500 mt-2">Failure Rate: {discoveryData.discovery_results.idoc_discovery.avg_failure_rate}%</div>
                  )}
                </div>
                <div className="bg-white border-2 border-purple-300 rounded-xl p-6">
                  <div className="text-3xl font-bold text-purple-600 mb-2">{discoveryData.discovery_results.idoc_discovery._web_services || discoveryData.discovery_results.web_services?.total_services || 0}</div>
                  <div className="text-sm text-gray-600">Web Services</div>
                  {discoveryData.discovery_results.idoc_discovery._real_based && (
                    <div className="mt-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      📊 Real Data
                    </div>
                  )}
                </div>
                <div className="bg-white border-2 border-green-300 rounded-xl p-6">
                  <div className="text-3xl font-bold text-green-600 mb-2">{discoveryData.discovery_results.idoc_discovery._rfc_services || 0}</div>
                  <div className="text-sm text-gray-600">RFC/BAPI Services</div>
                  {discoveryData.discovery_results.idoc_discovery._real_based && (
                    <div className="mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                      📊 Real Data
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Third-Party Integrations</h3>
                <div className="space-y-3">
                  {discoveryData.discovery_results.idoc_discovery.third_party_integrations?.map((integration, idx) => (
                    <div key={idx} className="border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-semibold text-gray-900">{integration.system}</h4>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">{integration.protocol}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            integration.criticality === 'Critical' ? 'bg-red-100 text-red-700' :
                            integration.criticality === 'High' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>{integration.criticality}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-700">{integration.volume_day}/day</span>
                      </div>
                      <p className="text-xs text-gray-500">S/4 Compatible: {integration.s4_compatible ? '✓ Yes' : '✗ No'}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Flat-File Integrations</h3>
                <div className="space-y-3">
                  {discoveryData.discovery_results.idoc_discovery.flat_file_integrations?.map((file, idx) => (
                    <div key={idx} className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{file.name}</h4>
                          <p className="text-sm text-gray-600">{file.frequency} | {file.file_type} | ~{file.avg_size_mb}MB</p>
                        </div>
                        {file.critical && <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Critical</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Workload Tab */}
          {activeTab === 'workload' && discoveryData.discovery_results.transaction_usage && (
            <div className="space-y-6">
              {/* Real Data Indicator for Workload */}
              {discoveryData.discovery_results.transaction_usage._real_based && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-green-800">
                        Real Data: {discoveryData.discovery_results.transaction_usage._calculation}
                      </span>
                    </div>
                    {discoveryData.discovery_results.transaction_usage._fiori_indicator && (
                      <span className="text-xs text-green-700">
                        {discoveryData.discovery_results.transaction_usage._fiori_indicator}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Top Transactions (Last {discoveryData.discovery_results.transaction_usage.analysis_period_days} days)</h3>
                <div className="space-y-3">
                  {discoveryData.discovery_results.transaction_usage.top_200_transactions?.slice(0, 10).map((tx, idx) => (
                    <div key={idx} className="border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{tx.tcode}</h4>
                          <p className="text-sm text-gray-600">{tx.description}</p>
                        </div>
                        <span className="text-xl font-bold text-blue-600">{tx.usage_count.toLocaleString()}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-xs text-gray-500">
                        <div>Users: {tx.unique_users}</div>
                        <div>Avg: {tx.avg_response_ms}ms</div>
                        <div>Peak: {tx.peak_response_ms}ms</div>
                        <div>Long-running: {tx.long_running_pct}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Long-Running Transactions</h3>
                <div className="space-y-3">
                  {discoveryData.discovery_results.transaction_usage.long_running_transactions?.map((tx, idx) => (
                    <div key={idx} className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{tx.tcode}</h4>
                        <span className="text-lg font-bold text-orange-600">{tx.avg_duration_sec}s avg</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">Max: {tx.max_duration_sec}s | Occurrences: {tx.occurrences}</p>
                      <p className="text-xs text-gray-500">Reason: {tx.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Batch Jobs Tab */}
          {activeTab === 'batch-jobs' && (discoveryData.discovery_results.batch_jobs || discoveryData.discovery_results.transaction_usage?.batch_jobs) && (
            <div className="space-y-6">
              {/* Real Data Indicator for Batch Jobs */}
              {discoveryData.discovery_results.batch_jobs?._real_based && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold text-green-800">
                      Real Data: {discoveryData.discovery_results.batch_jobs._calculation}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-blue-500 rounded-xl p-6 text-white">
                  <div className="text-3xl font-bold mb-2">{(discoveryData.discovery_results.batch_jobs || discoveryData.discovery_results.transaction_usage.batch_jobs).total_jobs}</div>
                  <div className="text-sm">Total Batch Jobs</div>
                  {discoveryData.discovery_results.batch_jobs?._real_based && (
                    <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded">
                      📊 Real Estimate
                    </div>
                  )}
                </div>
                <div className="bg-red-500 rounded-xl p-6 text-white">
                  <div className="text-3xl font-bold mb-2">{(discoveryData.discovery_results.batch_jobs || discoveryData.discovery_results.transaction_usage.batch_jobs).critical_jobs || (discoveryData.discovery_results.batch_jobs || discoveryData.discovery_results.transaction_usage.batch_jobs).high_volume_jobs?.length || 0}</div>
                  <div className="text-sm">Critical Jobs</div>
                  {discoveryData.discovery_results.batch_jobs?._real_based && (
                    <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded">
                      📊 Real Estimate
                    </div>
                  )}
                </div>
                <div className="bg-orange-500 rounded-xl p-6 text-white">
                  <div className="text-3xl font-bold mb-2">{discoveryData.discovery_results.transaction_usage?.batch_jobs?.failed_jobs_last_30d || 0}</div>
                  <div className="text-sm">Failed (30d)</div>
                </div>
                <div className="bg-yellow-500 rounded-xl p-6 text-white">
                  <div className="text-3xl font-bold mb-2">{discoveryData.discovery_results.transaction_usage?.batch_jobs?.jobs_over_sla || 0}</div>
                  <div className="text-sm">Over SLA</div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">High-Volume Batch Jobs</h3>
                <div className="space-y-3">
                  {discoveryData.discovery_results.transaction_usage.batch_jobs.high_volume_jobs.map((job, idx) => (
                    <div key={idx} className="border-2 border-gray-200 rounded-lg p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{job.job_name}</h4>
                          <p className="text-sm text-gray-600">{job.frequency} | Runtime: {job.avg_runtime_min} min</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          job.criticality === 'Critical' ? 'bg-red-100 text-red-700' :
                          job.criticality === 'High' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>{job.criticality}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                        <div>
                          <p className="text-gray-500">Records Processed</p>
                          <p className="font-semibold">{(job.records_processed / 1000000).toFixed(1)}M</p>
                        </div>
                        <div>
                          <p className="text-gray-500">S/4 Optimization</p>
                          <p className="font-semibold text-green-600">{job.s4_optimization_potential}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Top Users Tab */}
          {activeTab === 'users' && (discoveryData.discovery_results.top_users || discoveryData.discovery_results.transaction_usage?.most_active_users) && (
            <div className="space-y-6">
              {/* Real Data Indicator for Users */}
              {discoveryData.discovery_results.top_users?._real_based && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-green-800">
                        Real Data: {discoveryData.discovery_results.top_users._calculation}
                      </span>
                    </div>
                    <div className="text-xs text-green-700">
                      Total: {discoveryData.discovery_results.top_users.total_users} users, Active: {discoveryData.discovery_results.top_users.active_users}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Most Active Users (Top 5)</h3>
                <div className="space-y-3">
                  {discoveryData.discovery_results.transaction_usage.most_active_users.map((user, idx) => (
                    <div key={idx} className="border-2 border-blue-200 bg-blue-50 rounded-lg p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">{idx + 1}</div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{user.user_id}</h4>
                            <p className="text-sm text-gray-600">{user.role} - {user.department}</p>
                          </div>
                        </div>
                        <span className="text-2xl font-bold text-blue-600">{user.transaction_count.toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-gray-600">T-Codes used: {user.tcodes_used}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">User Training Needs</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600 mb-2">{discoveryData.discovery_results.transaction_usage.user_training_needs.total_users_needing_training}</div>
                    <div className="text-sm text-gray-700">Users Need Training</div>
                  </div>
                  {Object.entries(discoveryData.discovery_results.transaction_usage.user_training_needs.by_module).map(([module, count]) => (
                    <div key={module} className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                      <div className="text-xl font-bold text-gray-900 mb-2">{count}</div>
                      <div className="text-sm text-gray-600 capitalize">{module.replace(/_/g, ' ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LandscapeDiscovery;
